import { Alert } from 'lib/alert/models';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { STALE_TIME } from 'business/constants';

/**
 * Hook React Query to fetch the alerts owner by the user for the given source.
 * We need the source model here in order to cache the correct data.
 * @param {string} source - The source model.
 * @param {string | number} sourceId - The id of the source that carries the alerts
 */
export const useGetOwnedAlerts = (
    source: string,
    sourceId: string | number,
): UseQueryResult<Alert[]> =>
    useQuery({
        queryKey: [source, sourceId, 'alerts', 'owned'],

        queryFn: async () => {
            const response = await RQAPICallWithData<Alert[]>({
                endpoint: `alerts/${source}/${sourceId}/owned`,
                method: 'GET',
            });
            return response.content.data;
        },

        staleTime: STALE_TIME,
    });

/**
 * Hook React Query to fetch the subscribed alerts for the user and the given source.
 * We need the source model here in order to cache the correct data.
 * @param {string} source - The source model.
 * @param {string | number} sourceId - The id of the source that carries the alerts
 */
export const useGetSubscribedAlerts = (
    source: string,
    sourceId: string | number,
): UseQueryResult<Alert[]> =>
    useQuery({
        queryKey: [source, sourceId, 'alerts', 'subscribed'],

        queryFn: async () => {
            const response = await RQAPICallWithData<Alert[]>({
                endpoint: `alerts/${source}/${sourceId}/subscribed`,
                method: 'GET',
            });
            return response.content.data;
        },

        staleTime: STALE_TIME,
    });
